import type React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * Props for the StyledModal component.
 */
export type StyleModalProps = {
  /** Modal size breakpoint. */
  size?: 'sm' | 'lg' | 'xl';
  /** Controls the visibility of the modal. */
  show?: boolean;
  /** Optional title displayed in the modal header. */
  title?: string;
  /** Handler invoked when the modal is dismissed. */
  onClose?: () => void;
  /** When true, renders a default Close button in the footer. */
  footerCloseButton?: boolean;
  /** Additional custom footer buttons to render. */
  footerButtons?: React.ReactNode;
  /** Modal body content. */
  children: React.ReactNode;
};

/**
 * Styled modal wrapper with optional header and footer sections.
 *
 * @param size Modal size breakpoint.
 * @param show Controls whether the modal is visible.
 * @param title Optional header title; header renders only when provided.
 * @param onClose Close handler applied to dismiss controls.
 * @param footerCloseButton Adds a default Close button to the footer when true.
 * @param footerButtons Custom footer content rendered after the default button.
 * @param children Content rendered inside the modal body.
 */
export const StyledModal = ({
  size = 'lg',
  show = false,
  title,
  onClose,
  footerCloseButton = false,
  footerButtons,
  children,
}: StyleModalProps) => {
  const hasFooter = footerCloseButton || !!footerButtons;

  return (
    <Modal size={size} show={show} onHide={onClose}>
      {title && (
        <Modal.Header closeButton={!!onClose}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
      )}

      <Modal.Body>{children}</Modal.Body>

      {hasFooter && (
        <Modal.Footer>
          {footerCloseButton && (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
          {footerButtons}
        </Modal.Footer>
      )}
    </Modal>
  );
};
